import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import AdminFeatureFlagAlert from '@/components/AdminFeatureFlagAlert';
import DashboardHeader from '@/components/DashboardHeader';
import DashboardMetrics from '@/components/DashboardMetrics';
import DashboardCharts from '@/components/DashboardCharts';
import { useSmsBilling } from '@/hooks/sms/useSmsBilling';
import { useAnalyticsDashboard } from '@/hooks/useAnalyticsDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, RefreshCw, AlertTriangle, AlertCircle, HelpCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { SmsStatusIndicator } from '@/components/sms/SmsStatusIndicator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/auth';
import { UserRole } from '@/context/auth/types';
import { useRestaurantContext } from '@/context/RestaurantContext';
import RestaurantSelector from '@/components/analytics/RestaurantSelector';
import DebugBanner from '@/components/debug/DebugBanner';
import EnhancedDateRangePicker from '@/components/analytics/EnhancedDateRangePicker';
import { DateRange } from 'react-day-picker';
import OrderTypeFilter, { OrderTypeFilterValue } from '@/components/analytics/OrderTypeFilter';

// Import comparison components
import ComparisonCards from '@/components/analytics/ComparisonCards';
import CombinedComparisonChart from '@/components/analytics/charts/CombinedComparisonChart';
import CustomerSavingsChart from '@/components/analytics/charts/CustomerSavingsChart';
import ProfitIncreaseChart from '@/components/analytics/charts/ProfitIncreaseChart';
import PlatformComparisonTable from '@/components/analytics/tables/PlatformComparisonTable';
import RecentOrdersTable from '@/components/analytics/tables/RecentOrdersTable';

// Import individual chart components
import DailyOrdersChart from '@/components/analytics/charts/DailyOrdersChart';
import RevenueTrendChart from '@/components/analytics/charts/RevenueTrendChart';
import OrderTypeChart from '@/components/analytics/charts/OrderTypeChart';

const Dashboard = () => {
  const [timeframe, setTimeframe] = useState<string | DateRange>('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState<OrderTypeFilterValue>('all');
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { selectedRestaurants, setSelectedRestaurants } = useRestaurantContext();
  
  // Analytics data from our new hook
  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    error: analyticsError,
    dailyData,
    orderTypeData,
    refreshData: refreshAnalytics,
    changes,
    comparisonMetrics,
    comparisonData,
    feeComparisonData,
    restaurantMarkups
  } = useAnalyticsDashboard(timeframe, orderTypeFilter);
  
  // Check if there are delivery orders to show comparison components
  const hasDeliveryOrders = analyticsData?.rawData?.some(
    (order: any) => order.service_type === 'delivery'
  ) || false;
  
  // Check if user should have SMS access based on role
  const shouldShowSmsWidget = () => {
    if (!user || !user.role) return false;
    const onboardingRoles = [UserRole.NEW_SIGN_UP, UserRole.IN_PROGRESS];
    return !onboardingRoles.includes(user.role as UserRole);
  };

  // Always call useSmsBilling hook, but handle access control in render logic
  let smsData = { smsEnabled: false, usageData: null, loading: false, refreshData: () => {}, hasAccess: false };
  try {
    const result = useSmsBilling();
    const hasAccess = shouldShowSmsWidget();
    smsData = {
      smsEnabled: hasAccess ? result.smsEnabled : false,
      usageData: hasAccess ? (result.usageData || null) : null,
      loading: hasAccess ? result.loading : false,
      refreshData: hasAccess ? (result.refreshData || (() => {})) : (() => {}),
      hasAccess
    };
    
    if (!hasAccess) {
      console.log('Dashboard: User role does not have SMS access, suppressing SMS widget');
    }
  } catch (error) {
    console.warn('Dashboard: Failed to access SMS Billing context:', error);
  }
  
  const { smsEnabled, usageData, loading: smsLoading, refreshData, hasAccess } = smsData;
  
  // Handle restaurant selection change
  const handleRestaurantChange = (value: string) => {
    if (value === 'all') {
      setSelectedRestaurants(['all']);
    } else {
      setSelectedRestaurants([value]);
    }
  };
  
  // Only render the SMS usage widget if user has access and SMS is enabled
  const renderSmsWidget = () => {
    if (!hasAccess || !smsEnabled || !usageData) return null;

    const creditsUsed = usageData.currentPeriod?.units || 0;
    const totalCredits = 1000; // Assuming a default value, adjust based on your business logic
    const daysUntilRenewal = 30; // Placeholder, replace with actual calculation if available
    const usagePercentage = Math.min((creditsUsed / totalCredits) * 100, 100);
    
    // Determine warning level
    const isWarning = usagePercentage >= 80 && usagePercentage < 95;
    const isDanger = usagePercentage >= 95;
    
    // Determine progress color based on usage
    const progressColor = isDanger 
      ? "bg-red-500" 
      : isWarning 
        ? "bg-amber-500" 
        : "bg-primary";

    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2 text-primary" />
              SMS Credits Usage
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 ml-1">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="sr-only">SMS Credits Help</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>SMS credits are used when sending messages to customers. Each standard message uses 1 credit. Your plan includes 1,000 credits per month.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <SmsStatusIndicator compact showToggle={false} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="font-medium">Monthly Usage</p>
              <p className="font-medium">{creditsUsed} / {totalCredits}</p>
            </div>
            <Progress value={usagePercentage} className={`h-2 ${progressColor}`} />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Used: {usagePercentage.toFixed(0)}%</span>
              <span>Renews in: {daysUntilRenewal} days</span>
            </div>
            
            {/* Warning message for high usage */}
            {(isWarning || isDanger) && (
              <div className={`flex p-3 rounded-md ${isDanger ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
                <div className="mr-2 flex-shrink-0">
                  {isDanger ? 
                    <AlertCircle className="h-5 w-5 text-red-500" /> : 
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                  }
                </div>
                <div className="text-sm">
                  <p className="font-medium">
                    {isDanger ? 'Credits almost depleted!' : 'Credits running low'}
                  </p>
                  <p className={`mt-1 ${isDanger ? 'text-red-700' : 'text-amber-700'}`}>
                    {isDanger 
                      ? 'You have less than 5% of your monthly SMS credits remaining.' 
                      : 'You have used more than 80% of your monthly SMS credits.'}
                  </p>
                </div>
              </div>
            )}
            
            <div className={isMobile ? "flex flex-col gap-2" : "flex justify-end gap-2 mt-2"}>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2" 
                onClick={refreshData}
              >
                <RefreshCw className="h-3 w-3" />
                Refresh
              </Button>
              
              {(isWarning || isDanger) && (
                <Button 
                  variant={isDanger ? "default" : "outline"} 
                  size="sm"
                  className="gap-2"
                >
                  <MessageSquare className="h-3 w-3" />
                  Buy More Credits
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Navigation>
      <div className="space-y-6">
        <DashboardHeader 
          title="Reports Dashboard" 
          description="Overview of your restaurant performance"
        />
        
        {process.env.NODE_ENV !== 'production' && <DebugBanner />}
        
        <AdminFeatureFlagAlert />
        
        {/* Time Period Selector and Restaurant Selector */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Time period:</span>
              <EnhancedDateRangePicker
                value={timeframe}
                onValueChange={setTimeframe}
              />
            </div>
            
            {/* Restaurant selector to match Order Type filter */}
            {user?.restaurants && user.restaurants.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Restaurants:</span>
                <Select value={selectedRestaurants[0] || 'all'} onValueChange={handleRestaurantChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select restaurants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Restaurants</SelectItem>
                    {user.restaurants.map((restaurant) => (
                      <SelectItem key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Add the Order Type Filter */}
            <OrderTypeFilter
              value={orderTypeFilter}
              onValueChange={setOrderTypeFilter}
            />
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshAnalytics}
            disabled={analyticsLoading}
            className="gap-2"
          >
            <RefreshCw className={`h-3 w-3 ${analyticsLoading ? "animate-spin" : ""}`} />
            Refresh Data
          </Button>
        </div>
        
        {/* Error Alert */}
        {analyticsError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{analyticsError}</AlertDescription>
          </Alert>
        )}
        
        {/* Analytics Components */}
        <DashboardMetrics 
          data={analyticsData}
          changes={changes}
          isLoading={analyticsLoading}
        />
        
        {/* Charts and Comparison Cards */}
        {hasDeliveryOrders && !analyticsLoading && analyticsData && (
          <>
            <div className="grid gap-6 md:grid-cols-2">
              <ProfitIncreaseChart data={dailyData} />
              <CustomerSavingsChart data={dailyData} />
            </div>
            <ComparisonCards
              totalCustomerSavings={comparisonMetrics.totalCustomerSavings}
              totalRestaurantProfit={comparisonMetrics.totalRestaurantProfit}
              avgCustomerSavings={comparisonMetrics.avgCustomerSavings}
              avgRestaurantProfit={comparisonMetrics.avgRestaurantProfit}
            />
          </>
        )}
        
        {/* Platform Comparison Chart and Table */}
        {hasDeliveryOrders && !analyticsLoading && analyticsData && (
          <div className="grid gap-6 md:grid-cols-2">
            <CombinedComparisonChart 
              comparisonMetrics={comparisonMetrics}
              feeComparisonData={feeComparisonData}
              rawData={analyticsData.rawData}
            />
            <PlatformComparisonTable 
              metrics={comparisonMetrics} 
              rawData={analyticsData.rawData}
              restaurantMarkups={restaurantMarkups}
              selectedRestaurants={selectedRestaurants}
            />
          </div>
        )}
        
        {/* Daily Orders Chart */}
        {!analyticsLoading && dailyData.length > 0 && (
          <DailyOrdersChart data={dailyData} />
        )}
        
        
        {/* Recent Orders Table */}
        {!analyticsLoading && analyticsData && analyticsData.rawData && (
          <RecentOrdersTable 
            orders={analyticsData.rawData} 
            showComparison={hasDeliveryOrders} 
          />
        )}
        
        {/* SMS Usage Widget with improved loading behavior */}
        {renderSmsWidget()}
      </div>
    </Navigation>
  );
};

export default Dashboard;
